import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
// hooks
import { useProfile } from 'src/hooks/use-profile';
// utils
import { fData } from 'src/utils/format-number';
import { normalizePhone, phoneSchema } from 'src/utils/phone';
// assets
// components
import FormProvider, {
  RHFPhoneField,
  RHFTextField,
  RHFUploadAvatar
} from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import { useDeleteFileMutation, useUploadNewFileMutation } from 'src/store/Reducer/file';
import { useUpdateProfileMutation } from 'src/store/Reducer/settings';
import { USER_TYPE_SHOPADMIN, USER_TYPE_SUPER_ADMIN } from 'src/config-global';

// ----------------------------------------------------------------------

export default function AccountGeneral() {

  const { enqueueSnackbar } = useSnackbar();

  const { profile: user, role: profileRole } = useProfile();

  const [updateProfile] = useUpdateProfileMutation();

  const [uploadNewFile] = useUploadNewFileMutation();

  const [deleteFile] = useDeleteFileMutation();

  const UpdateUserSchema = Yup.object().shape({
    displayName: Yup.string().required('Name is required'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    photoURL: Yup.mixed().nullable(),
    phoneNumber: phoneSchema(),
  });

  const defaultValues = useMemo(
    () => ({
      displayName: user?.displayName || user?.name || '',
      email: user?.email || '',
      photoURL: user?.photoURL || user?.image || null,
      phoneNumber: normalizePhone(user?.phone),
    }),
    [user?.displayName, user?.name, user?.email, user?.photoURL, user?.image, user?.phone]
  );

  const role = profileRole;

  const methods = useForm({
    resolver: yupResolver(UpdateUserSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },

  } = methods;

  // Re-seed the form whenever the Get Profile API returns fresh data.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit(async (data) => {
    let imageURL = "";

    try {
      if (data.photoURL && typeof data.photoURL !== 'string') {
        const pastFileName = (user?.photoURL || user?.image)?.split('/').pop();

        if (pastFileName && pastFileName !== 'noimg.png') {
          const deleteFileResponse = await deleteFile({ fileName: pastFileName });

          if (deleteFileResponse?.error) {
            console.error('Failed to delete old file:', deleteFileResponse.error);
            return;
          }
        }

        const uploadedFileResponse = await uploadNewFile({ file: data.photoURL });

        if (!uploadedFileResponse?.error) {
          imageURL = uploadedFileResponse?.data?.body?.fileName;
        }
      }

      // No new upload → keep the current image; send just the filename the backend stores.
      const currentImageName = (user?.image || user?.photoURL)?.split('/').pop();

      // Only send the phone when it actually changed, so the backend's
      // "phone already exists" uniqueness check isn't triggered by the user's own number.
      const phone = normalizePhone(data.phoneNumber);
      const phoneChanged = phone && phone !== normalizePhone(user?.phone);

      const response = await updateProfile({
        role,
        data: {
          name: data.displayName,
          ...(phoneChanged && { phone }),
          image: imageURL || currentImageName,
        },
      });

      if (!response?.error) {
        enqueueSnackbar('Profile updated successfully');
        // The 'Profile' RTK tag is invalidated by the mutation → useProfile / useProfileSync
        // refetch and propagate the fresh data to this form, the Header and the sidebar.
      }
    } catch (error) {
      console.error(error);
    }
  });

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('photoURL', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );


  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12} md={4}>
          <Card sx={{ pt: 10, pb: 5, px: 3, textAlign: 'center' }}>
            <RHFUploadAvatar
              name="photoURL"
              maxSize={3145728}
              onDrop={handleDrop}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 3,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  Allowed *.jpeg, *.jpg, *.png, *.gif
                  <br /> max size of {fData(3145728)}
                </Typography>
              }
            />
          </Card>
        </Grid>

        <Grid xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(1, 1fr)',
              }}
            >
              <RHFTextField name="displayName" label="Name" />
              <RHFTextField name="email" label="Email Address" InputProps={{
                readOnly: true,
              }} />
              {user?.user_type !== USER_TYPE_SUPER_ADMIN && user?.user_type!==USER_TYPE_SHOPADMIN && <RHFPhoneField name="phoneNumber" label="Phone Number" />}
            </Box>

            <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Save Changes
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
